'use client';

import {
  type ColumnDef,
  type ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type Row,
  type RowSelectionState,
  type SortingState,
  useReactTable,
  type VisibilityState,
} from '@tanstack/react-table';
import type { Dispatch, SetStateAction } from 'react';
import { useState } from 'react';

const NO_PINNING: { left?: string[] } = {};

// gloss
type UseDataTableProps<TData> = {
  data: TData[];
  columns: ColumnDef<TData, unknown>[];
  getRowId?: (originalRow: TData, index: number, parent?: Row<TData>) => string;
  enableSorting?: boolean;
  enableFiltering?: boolean;
  enablePagination?: boolean;
  enableRowSelection?: boolean;
  enableColumnResizing?: boolean;
  enableColumnVisibility?: boolean;
  manualSorting?: boolean;
  manualFiltering?: boolean;
  manualPagination?: boolean;
  pageCount?: number;
  rowCount?: number;
  sorting?: SortingState;
  onSortingChange?: Dispatch<SetStateAction<SortingState>>;
  columnFilters?: ColumnFiltersState;
  onColumnFiltersChange?: Dispatch<SetStateAction<ColumnFiltersState>>;
  pagination?: PaginationState;
  onPaginationChange?: Dispatch<SetStateAction<PaginationState>>;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: Dispatch<SetStateAction<RowSelectionState>>;
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: Dispatch<SetStateAction<VisibilityState>>;
  columnPinning?: { left?: string[] };
  globalFilter?: string;
  onGlobalFilterChange?: Dispatch<SetStateAction<string>>;
  initialSorting?: SortingState;
  initialPagination?: PaginationState;
  initialColumnVisibility?: VisibilityState;
};

// gloss
const useDataTable = <TData>({
  data,
  columns,
  getRowId,
  enableSorting = true,
  enableFiltering = true,
  enablePagination = true,
  enableRowSelection = false,
  enableColumnResizing = false,
  enableColumnVisibility = false,
  manualSorting = false,
  manualFiltering = false,
  manualPagination = false,
  pageCount,
  rowCount,
  sorting: sortingProp,
  onSortingChange: onSortingChangeProp,
  columnFilters: columnFiltersProp,
  onColumnFiltersChange: onColumnFiltersChangeProp,
  pagination: paginationProp,
  onPaginationChange: onPaginationChangeProp,
  rowSelection: rowSelectionProp,
  onRowSelectionChange: onRowSelectionChangeProp,
  columnVisibility: columnVisibilityProp,
  onColumnVisibilityChange: onColumnVisibilityChangeProp,
  columnPinning = NO_PINNING,
  globalFilter: globalFilterProp,
  onGlobalFilterChange: onGlobalFilterChangeProp,
  initialSorting = [],
  initialPagination = { pageIndex: 0, pageSize: 10 },
  initialColumnVisibility = {},
}: UseDataTableProps<TData>) => {
  const [internalSorting, setInternalSorting] = useState<SortingState>(initialSorting);
  const [internalColumnFilters, setInternalColumnFilters] = useState<ColumnFiltersState>([]);
  const [internalPagination, setInternalPagination] = useState<PaginationState>(initialPagination);
  const [internalRowSelection, setInternalRowSelection] = useState<RowSelectionState>({});
  const [internalColumnVisibility, setInternalColumnVisibility] = useState<VisibilityState>(initialColumnVisibility);
  const [internalGlobalFilter, setInternalGlobalFilter] = useState('');

  const sorting = sortingProp ?? internalSorting;
  const onSortingChange = onSortingChangeProp ?? setInternalSorting;
  const columnFilters = columnFiltersProp ?? internalColumnFilters;
  const onColumnFiltersChange = onColumnFiltersChangeProp ?? setInternalColumnFilters;
  const pagination = paginationProp ?? internalPagination;
  const onPaginationChange = onPaginationChangeProp ?? setInternalPagination;
  const rowSelection = rowSelectionProp ?? internalRowSelection;
  const onRowSelectionChange = onRowSelectionChangeProp ?? setInternalRowSelection;
  const columnVisibility = columnVisibilityProp ?? internalColumnVisibility;
  const onColumnVisibilityChange = onColumnVisibilityChangeProp ?? setInternalColumnVisibility;
  const globalFilter = globalFilterProp ?? internalGlobalFilter;
  const setGlobalFilter = onGlobalFilterChangeProp ?? setInternalGlobalFilter;

  const table = useReactTable({
    data,
    columns,
    getRowId,
    state: {
      sorting,
      columnFilters,
      pagination,
      rowSelection,
      columnVisibility,
      columnPinning,
      globalFilter,
    },
    onSortingChange,
    onColumnFiltersChange,
    onPaginationChange,
    onRowSelectionChange,
    onColumnVisibilityChange,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    ...(enableSorting && { getSortedRowModel: getSortedRowModel() }),
    ...(enableFiltering && { getFilteredRowModel: getFilteredRowModel() }),
    ...(enablePagination && { getPaginationRowModel: getPaginationRowModel() }),
    enableSorting,
    enableFilters: enableFiltering,
    enableRowSelection,
    enableColumnResizing,
    enableHiding: enableColumnVisibility,
    manualSorting,
    manualFiltering,
    manualPagination,
    ...(manualPagination && pageCount !== undefined && { pageCount }),
    ...(manualPagination && rowCount !== undefined && { rowCount }),
    columnResizeMode: enableColumnResizing ? 'onChange' : undefined,
  });

  return {
    table,
    sorting,
    columnFilters,
    pagination,
    rowSelection,
    columnVisibility,
    globalFilter,
    setGlobalFilter,
  };
};

export type { UseDataTableProps };
export { useDataTable };
